import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import BookNpcCard from "../components/book/BookNpcCard";
import BookNpcForm from "../components/book/BookNpcForm";
import { BookOpen, ScrollText, X, Plus, ChevronUp, ChevronDown, Trash2, UserPlus } from "lucide-react";

const DESK_BG = "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/1b346054bea1426ee0b871f19ac741cdc385839fb8708409e7ce88e9297851d3.jpeg";
const PARCHMENT_BG = "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/56a8205ec2b60e434d830e2dcdddf3fcd4e0e2214aee12cb4b4c6b7866877934.jpeg";
const LEATHER_BG = "https://static.prod-images.emergentagent.com/jobs/4aedbd9c-cc9b-4c85-b42d-35c258d0324d/images/980d57aa94697dd4222bd2af352f81306990bc1f124ad060ccdd1916029d267d.jpeg";

export default function BookPage() {
  const { gameId, bookType } = useParams();
  const [book, setBook] = useState(null);
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [monsterTemplates, setMonsterTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [contentDraft, setContentDraft] = useState("");
  const [npcFormFor, setNpcFormFor] = useState(null); // 'new' | npc object | null

  const activePage = pages.find((p) => p.id === activePageId) || null;
  const base = `/games/${gameId}/books/${bookType}`;

  const load = useCallback(async () => {
    try {
      const { data: b } = await api.get(base);
      setBook(b);
      setTitleDraft(b.title);
      const { data: p } = await api.get(`${base}/${b.id}/pages`);
      setPages(p);
      if (p.length && !activePageId) setActivePageId(p[0].id);
    } catch {
      setDenied(true);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/games/${gameId}/monster-templates`).then((r) => setMonsterTemplates(r.data)).catch(() => setMonsterTemplates([]));
  }, [gameId]);
  useEffect(() => { setContentDraft(activePage?.content || ""); }, [activePageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBookTitle = async () => {
    setEditingTitle(false);
    if (!titleDraft.trim() || titleDraft === book.title) return;
    const { data } = await api.patch(`${base}/${book.id}`, { title: titleDraft });
    setBook(data);
  };

  const addChapter = async () => {
    const { data } = await api.post(`${base}/${book.id}/pages`, { title: `Chapter ${pages.length + 1}`, content: "" });
    setPages((prev) => [...prev, data]);
    setActivePageId(data.id);
  };

  const patchPage = async (pageId, updates) => {
    const { data } = await api.patch(`${base}/${book.id}/pages/${pageId}`, updates);
    setPages((prev) => prev.map((p) => (p.id === pageId ? data : p)));
    return data;
  };

  const saveContent = () => { if (activePage) patchPage(activePage.id, { content: contentDraft }); };

  const renameChapter = (pageId, title) => patchPage(pageId, { title });

  const deleteChapter = async (pageId) => {
    await api.delete(`${base}/${book.id}/pages/${pageId}`);
    const remaining = pages.filter((p) => p.id !== pageId);
    setPages(remaining);
    if (activePageId === pageId) setActivePageId(remaining[0]?.id || null);
  };

  const moveChapter = async (pageId, dir) => {
    const idx = pages.findIndex((p) => p.id === pageId);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= pages.length) return;
    const a = pages[idx], b2 = pages[swapIdx];
    await patchPage(a.id, { order: b2.order });
    await patchPage(b2.id, { order: a.order });
    setPages((prev) => [...prev].sort((x, y) => x.order - y.order));
  };

  const saveNpc = async (npcData) => {
    if (!activePage) return;
    const existing = activePage.npcs || [];
    const npcs = npcData.id ? existing.map((n) => (n.id === npcData.id ? npcData : n)) : [...existing, npcData];
    await patchPage(activePage.id, { npcs });
    setNpcFormFor(null);
    toast.success("Character saved to this chapter");
  };

  const deleteNpc = async (npcId) => {
    if (!activePage) return;
    await patchPage(activePage.id, { npcs: (activePage.npcs || []).filter((n) => n.id !== npcId) });
  };

  if (loading) return <div className="min-h-screen bg-[#1a1008] flex items-center justify-center text-[#f1e0c5]/50 font-display">Opening the book…</div>;

  if (denied) {
    return (
      <div className="min-h-screen bg-[#1a1008] flex items-center justify-center text-center p-8">
        <div>
          <BookOpen className="w-10 h-10 text-[#D4AF37]/40 mx-auto mb-3" />
          <p className="font-display text-xl text-[#f1e0c5]/70">This tome is sealed</p>
          <p className="text-sm text-[#f1e0c5]/40 mt-1">Only the GM running this table may open it.</p>
        </div>
      </div>
    );
  }

  const Icon = bookType === "lore" ? ScrollText : BookOpen;

  return (
    <div className="min-h-screen bg-cover bg-center flex flex-col" style={{ backgroundImage: `url(${DESK_BG})` }} data-testid="book-page">
      <div className="min-h-screen flex-1 flex flex-col bg-black/20">
        <header className="flex items-center gap-3 px-4 py-3 border-b border-[#D4AF37]/20 bg-black/60">
          <Icon className="w-5 h-5 text-[#D4AF37] shrink-0" />
          {editingTitle ? (
            <Input
              data-testid="book-title-input"
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveBookTitle}
              onKeyDown={(e) => e.key === "Enter" && saveBookTitle()}
              className="bg-black/30 border-white/10 h-8 max-w-xs font-display"
            />
          ) : (
            <button data-testid="book-title-edit" onClick={() => setEditingTitle(true)} className="font-display text-lg text-[#f1e0c5] hover:text-[#D4AF37] transition-colors duration-150 truncate">
              {book.title}
            </button>
          )}
          <div className="flex-1" />
          <button data-testid="book-close-button" onClick={() => window.close()} className="text-[#f1e0c5]/50 hover:text-[#D90429] transition-colors duration-150">
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 flex overflow-hidden p-4 md:p-10 gap-3 md:gap-4">
          <aside
            className="w-48 md:w-56 shrink-0 rounded-xl p-3 space-y-1.5 overflow-y-auto book-page-scrollbar bg-cover"
            style={{ backgroundImage: `url(${LEATHER_BG})` }}
            data-testid="book-chapter-list"
          >
            {pages.map((p, idx) => (
              <div key={p.id} className={`rounded-lg overflow-hidden ${p.id === activePageId ? "ring-2 ring-[#D4AF37]" : ""}`}>
                <button
                  data-testid={`book-chapter-select-${p.id}`}
                  onClick={() => setActivePageId(p.id)}
                  className={`w-full text-left px-2.5 py-2 text-xs font-book truncate ${p.id === activePageId ? "bg-[#D4AF37]/25 text-[#fff4da]" : "bg-black/40 text-[#f1e0c5]/80 hover:bg-black/55"}`}
                >
                  {p.title}
                </button>
                {p.id === activePageId && (
                  <div className="flex items-center justify-between bg-black/50 px-1.5 py-1">
                    <div className="flex gap-0.5">
                      <button data-testid={`book-chapter-up-${p.id}`} onClick={() => moveChapter(p.id, -1)} disabled={idx === 0} className="text-[#f1e0c5]/50 hover:text-[#D4AF37] disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button data-testid={`book-chapter-down-${p.id}`} onClick={() => moveChapter(p.id, 1)} disabled={idx === pages.length - 1} className="text-[#f1e0c5]/50 hover:text-[#D4AF37] disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>
                    <button data-testid={`book-chapter-delete-${p.id}`} onClick={() => deleteChapter(p.id)} className="text-[#f1e0c5]/40 hover:text-[#D90429]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            ))}
            <Button data-testid="book-add-chapter-button" onClick={addChapter} size="sm" variant="secondary" className="w-full rounded-full gap-1.5 mt-2">
              <Plus className="w-3.5 h-3.5" /> New Chapter
            </Button>
          </aside>

          <main
            className="flex-1 overflow-y-auto book-page-scrollbar rounded-xl p-6 md:p-10 bg-cover shadow-2xl"
            style={{ backgroundImage: `url(${PARCHMENT_BG})` }}
            data-testid="book-main-content"
          >
            {!activePage ? (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <p className="font-display text-xl text-[#3b2a1a] mb-3">This tome is empty</p>
                  <Button data-testid="book-add-first-chapter-button" onClick={addChapter} className="rounded-full bg-[#8b1a1a] hover:bg-[#6e1414]">
                    <Plus className="w-4 h-4 mr-1.5" /> Write Your First Chapter
                  </Button>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto space-y-5">
                <Input
                  data-testid="book-chapter-title-input"
                  value={activePage.title}
                  onChange={(e) => setPages((prev) => prev.map((p) => (p.id === activePage.id ? { ...p, title: e.target.value } : p)))}
                  onBlur={(e) => renameChapter(activePage.id, e.target.value)}
                  className="bg-transparent border-0 border-b-2 border-[#8b5a2b]/40 rounded-none px-0 font-display text-2xl md:text-3xl text-[#3b2a1a] focus-visible:ring-0"
                />
                <Textarea
                  data-testid="book-content-textarea"
                  value={contentDraft}
                  onChange={(e) => setContentDraft(e.target.value)}
                  onBlur={saveContent}
                  placeholder="Write your campaign's story here…"
                  className="font-book text-lg leading-relaxed bg-transparent border-0 text-[#2c1e13] placeholder:text-[#8b5a2b]/40 focus-visible:ring-0 min-h-[40vh] resize-none px-0"
                />

                <div className="border-t border-[#8b5a2b]/30 pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-display text-sm uppercase tracking-wider text-[#5c3a1a]">Cast of This Chapter</p>
                    <Button data-testid="book-add-npc-button" onClick={() => setNpcFormFor("new")} size="sm" variant="secondary" className="rounded-full gap-1.5 h-7 text-xs">
                      <UserPlus className="w-3.5 h-3.5" /> Add Character
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {(activePage.npcs || []).map((npc) => (
                      <BookNpcCard key={npc.id} npc={npc} onEdit={(n) => setNpcFormFor(n)} onDelete={deleteNpc} />
                    ))}
                  </div>
                  {(!activePage.npcs || activePage.npcs.length === 0) && !npcFormFor && (
                    <p className="text-xs text-[#5c3a1a]/50 italic">No characters added to this chapter yet.</p>
                  )}
                  {npcFormFor && (
                    <BookNpcForm
                      gameId={gameId}
                      initial={npcFormFor === "new" ? null : npcFormFor}
                      monsterTemplates={monsterTemplates}
                      onSave={saveNpc}
                      onCancel={() => setNpcFormFor(null)}
                    />
                  )}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
